import React, { useState, useCallback, FormEvent } from 'react';
import { useAuth } from '../../hooks/auth';
import * as Yup from 'yup';
import getValidationErrors from '../../utils/getValidationErrors';
import { Form, InputForm, Button, ErrorText, ErrorContainer, AlternativeText, SocialButtonsContainer, Divider } from './styles';
import GoogleAuthButton from '../GoogleAuthButton';

interface IErrors {
  phone?: string;
}

interface PhoneAuthFormProps {
  onPhoneSubmitted: (phone: string) => void;
  showSocialButtons?: boolean;
}

const PhoneAuthForm: React.FC<PhoneAuthFormProps> = ({
  onPhoneSubmitted,
  showSocialButtons = true
}) => {
  const { sendPhoneOtp } = useAuth();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as IErrors);
  const [apiError, setApiError] = useState('');

  const getErrorMessage = (error: any): string => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message || error?.message;

    if (status === 400 || status === 404) {
      return 'Não conseguimos enviar o código de verificação. O número pode estar inválido ou o serviço de WhatsApp pode estar indisponível no momento.';
    }
    if (status === 500 || status === 503) {
      return 'Serviço temporariamente indisponível. Por favor, tente novamente em alguns minutos ou use outra forma de login.';
    }
    if (status === 429) {
      return 'Muitas tentativas. Por favor, aguarde alguns minutos antes de tentar novamente.';
    }

    return message || 'Erro ao enviar código. Por favor, tente novamente ou use outra forma de login.';
  };

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      setErrors({});
      setApiError('');

      const cleanPhone = phone.replace(/\D/g, '');

      if (!cleanPhone) {
        setErrors({ phone: 'Número de celular obrigatório' });
        setLoading(false);
        return;
      }

      const data = { phone: cleanPhone };

      try {
        const schema = Yup.object().shape({
          phone: Yup.string()
            .required('Número de celular obrigatório')
            .min(10, 'Número deve ter pelo menos 10 dígitos')
            .max(15, 'Número inválido'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await sendPhoneOtp({ phone: cleanPhone });

        onPhoneSubmitted(cleanPhone);

        setLoading(false);
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const findedErrors = getValidationErrors(err);
          setErrors(findedErrors);
        } else {
          const errorMsg = getErrorMessage(err);
          setApiError(errorMsg);
        }
        setLoading(false);
      }
    },
    [phone, sendPhoneOtp, onPhoneSubmitted]
  );

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');

    if (cleaned.length === 0) return '';
    if (cleaned.length <= 2) return `(${cleaned}`;
    if (cleaned.length <= 7) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  };

  return (
    <Form onSubmit={handleSubmit}>
      {showSocialButtons && (
        <>
          <SocialButtonsContainer>
            <GoogleAuthButton />
          </SocialButtonsContainer>

          <Divider>OU</Divider>
        </>
      )}

      {apiError && (
        <ErrorContainer>
          <ErrorText>{apiError}</ErrorText>
          <AlternativeText>
            Se o problema persistir, tente fazer login usando seu email e senha na aba abaixo.
          </AlternativeText>
        </ErrorContainer>
      )}

      <label>
        Celular: <span style={{ color: '#FF3838' }}>{errors.phone}</span>
      </label>
      <InputForm
        hasError={!!errors.phone}
        type="tel"
        name="phone"
        placeholder="(11) 99999-9999"
        value={formatPhoneNumber(phone)}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '');
          setPhone(value);
        }}
        maxLength={15}
        disabled={loading}
      />

      {!loading ? (
        <Button type="submit">ENVIAR CÓDIGO</Button>
      ) : (
        <Button type="button" disabled>
          Enviando...
        </Button>
      )}
    </Form>
  );
};

export default PhoneAuthForm;